/**
 * Location data transformers and hierarchy utilities
 */

/**
 * Build a hierarchical tree from a flat array of locations
 * @param {Array} flatLocations - Flat array of location objects
 * @returns {Array} Tree-structured locations with children arrays
 */
export const buildLocationHierarchy = (flatLocations) => {
  if (!Array.isArray(flatLocations) || flatLocations.length === 0) return [];

  const locationMap = {};
  flatLocations.forEach((loc) => {
    const locId = loc.id || loc._id || loc.location_UUID;
    locationMap[locId] = { ...loc, children: [] };
  });

  const roots = [];
  flatLocations.forEach((loc) => {
    const locId = loc.id || loc._id || loc.location_UUID;
    const parentId = loc.parentLocation || loc.parent_id || loc.parentId;

    if (parentId && locationMap[parentId]) {
      locationMap[parentId].children.push(locationMap[locId]);
    } else {
      roots.push(locationMap[locId]);
    }
  });

  return roots;
};

/**
 * Flatten a hierarchical tree back to a flat array
 * @param {Array} hierarchy - Tree-structured locations
 * @returns {Array} Flat array of all locations
 */
export const flattenHierarchy = (hierarchy) => {
  const result = [];
  const traverse = (nodes) => {
    for (const node of nodes) {
      const { children, ...rest } = node;
      result.push(rest);
      if (children && children.length > 0) {
        traverse(children);
      }
    }
  };
  traverse(hierarchy);
  return result;
};

/**
 * Get the ID of a location, handling multiple formats
 * @param {Object} location - Location object
 * @returns {string|number|null} Location ID
 */
export const getLocationId = (location) => {
  if (!location) return null;
  return location.id || location._id || location.location_UUID;
};

/**
 * Get the parent ID of a location, handling multiple formats
 * @param {Object} location - Location object
 * @returns {string|number|null} Parent location ID
 */
export const getParentId = (location) => {
  if (!location) return null;
  return location.parentLocation || location.parent_id || location.parentId;
};

/**
 * Get the display name of a location
 * @param {Object} location - Location object
 * @returns {string} Display name
 */
export const getLocationName = (location) => {
  if (!location) return 'Unknown';
  return location.friendlyName || location.name || `Location ${getLocationId(location)}`;
};

/**
 * Get the type ID of a location, handling nested objects
 * @param {Object} location - Location object
 * @returns {number|null} Location type ID
 */
export const getLocationTypeId = (location) => {
  if (!location) return null;
  if (location.locationTypeModel?.id) return location.locationTypeModel.id;
  if (location.locationTypeModel?.value) return location.locationTypeModel.value;
  if (location.locationType) {
    return typeof location.locationType === 'object' ? location.locationType.id : location.locationType;
  }
  return null;
};

/**
 * Get the type name of a location
 * @param {Object} location - Location object
 * @returns {string} Type name
 */
export const getLocationTypeName = (location) => {
  if (!location) return 'Unknown';
  return location.locationTypeModel?.friendlyName ||
    location.locationTypeModel?.name ||
    'Unknown Type';
};

/**
 * Find a location by ID in a hierarchical tree
 * @param {Array} nodes - Tree-structured locations
 * @param {string|number} id - ID to search for
 * @returns {Object|null} Found location or null
 */
export const findLocationById = (nodes, id) => {
  if (!nodes || !id) return null;
  for (const node of nodes) {
    const nodeId = getLocationId(node);
    if (nodeId === parseInt(id) || nodeId === id || String(nodeId) === String(id)) {
      return node;
    }
    if (node.children && node.children.length > 0) {
      const found = findLocationById(node.children, id);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Compute the breadcrumb path from root to a specific location
 * @param {Array} flatLocations - Flat array of all locations
 * @param {string|number} locationId - Target location ID
 * @returns {Array} Array of {id, name, typeId} from root to target
 */
export const computeBreadcrumbPath = (flatLocations, locationId) => {
  if (!flatLocations || !locationId) return [];

  // Build a lookup map
  const map = {};
  flatLocations.forEach(loc => {
    const id = getLocationId(loc);
    map[id] = loc;
  });

  const path = [];
  const visited = new Set();
  let current = map[locationId] || map[String(locationId)] || map[Number(locationId)];

  while (current) {
    const currentId = getLocationId(current);
    // Cycle detection: stop if we've visited this node before
    if (visited.has(String(currentId))) break;
    visited.add(String(currentId));

    path.unshift({
      id: currentId,
      name: getLocationName(current),
      typeId: getLocationTypeId(current),
      typeName: getLocationTypeName(current)
    });
    const parentId = getParentId(current);
    current = parentId ? (map[parentId] || map[String(parentId)] || map[Number(parentId)]) : null;
  }

  return path;
};

/**
 * Get direct children of a location from the hierarchy
 * @param {Array} hierarchy - Tree-structured locations
 * @param {string|number|null} parentId - Parent location ID (null for roots)
 * @returns {Array} Direct children
 */
export const getChildrenOf = (hierarchy, parentId) => {
  if (!parentId) return hierarchy; // Return roots

  const parent = findLocationById(hierarchy, parentId);
  return parent?.children || [];
};

/**
 * Get siblings of a location (other children of the same parent)
 * @param {Array} flatLocations - Flat array of all locations
 * @param {Object} location - The location to find siblings for
 * @returns {Array} Array of sibling locations (including the location itself)
 */
export const getSiblings = (flatLocations, location) => {
  if (!location || !flatLocations) return [];

  const parentId = getParentId(location);
  if (!parentId) {
    // Root-level siblings: all locations without parents
    return flatLocations.filter(loc => !getParentId(loc));
  }

  return flatLocations.filter(loc => {
    const locParentId = getParentId(loc);
    return String(locParentId) === String(parentId);
  });
};

/**
 * Count total descendants (children, grandchildren, etc.) of a location
 * @param {Object} location - Location with children array
 * @returns {number} Total descendant count
 */
export const countDescendants = (location) => {
  if (!location?.children || location.children.length === 0) return 0;
  let count = location.children.length;
  for (const child of location.children) {
    count += countDescendants(child);
  }
  return count;
};

/**
 * Filter locations by type ID
 * @param {Array} locations - Array of locations
 * @param {number} typeId - Type ID to filter by
 * @returns {Array} Filtered locations
 */
export const filterByType = (locations, typeId) => {
  if (!typeId) return locations;
  return locations.filter(loc => getLocationTypeId(loc) === typeId);
};

/**
 * Search locations by name (case-insensitive)
 * @param {Array} locations - Array of locations
 * @param {string} searchTerm - Search string
 * @returns {Array} Matching locations
 */
export const searchLocations = (locations, searchTerm) => {
  if (!searchTerm || !searchTerm.trim()) return locations;
  const term = searchTerm.toLowerCase().trim();
  return locations.filter(loc => {
    const name = getLocationName(loc).toLowerCase();
    const typeName = getLocationTypeName(loc).toLowerCase();
    return name.includes(term) || typeName.includes(term);
  });
};

/**
 * Extract flat locations from API response (handles multiple formats)
 * @param {Object} response - API response object
 * @returns {{ flatLocations: Array, total: number }}
 */
export const extractLocationsFromResponse = (response) => {
  let flatLocations = [];
  let total = 0;

  if (response?.data?.data && Array.isArray(response.data.data)) {
    flatLocations = response.data.data;
    total = response.data.total || flatLocations.length;
  } else if (response?.data?.locations && Array.isArray(response.data.locations)) {
    flatLocations = response.data.locations;
    total = response.data.total || flatLocations.length;
  } else if (response?.data?.docs && Array.isArray(response.data.docs)) {
    flatLocations = response.data.docs;
    total = response.data.totalDocs || response.data.total || flatLocations.length;
  } else if (response?.data && Array.isArray(response.data)) {
    flatLocations = response.data;
    total = flatLocations.length;
  } else if (Array.isArray(response)) {
    flatLocations = response;
    total = flatLocations.length;
  }

  return { flatLocations, total };
};

/**
 * Filter locations by site (using siteLocationIds)
 * @param {Array} locations - Flat array of locations
 * @param {Array} siteLocationIds - Array of allowed location IDs
 * @returns {Array} Filtered locations
 */
export const filterBySite = (locations, siteLocationIds) => {
  if (!siteLocationIds || !Array.isArray(siteLocationIds)) return locations;

  return locations.filter(loc => {
    const locId = getLocationId(loc);
    return siteLocationIds.includes(Number(locId)) || siteLocationIds.includes(String(locId));
  });
};
